/**
 * Тайлангийн файлууд — AI туслахаас асуухад гаргадаг Excel (.xlsx).
 *
 * Эзэмшигчийн шийдвэр (2026-10-06): ажилтан хүссэн баримт, тайланг
 * ЗӨВХӨН AI туслахаар дамжуулж авна, файл нь ҮРГЭЛЖ бүх хугацааг
 * хамарна. AI зөвхөн аль тайлан болохыг сонгоно — файлыг сервер DB-ээс
 * шууд бүтээнэ (тоо зохиогдохгүй, файлын мөрүүд AI руу явахгүй).
 *
 * Энэ файл ЦЭВЭР: каталог (`REPORT_FILES`) ба ерөнхий хүснэгт →
 * `XlsxSheet` хувиргалт. Мөрүүдийг `services/core` бэлдэнэ.
 *
 * ⚠️ Мөнгө string-ээр орж ирнэ (`"450000.00"`) — Float болгохгүй,
 *    нийлбэрийг `decimal.js`-ээр бодно (CLAUDE.md №3).
 * ⚠️ Баркод ТЕКСТ — 12 оронтой код `4.46E+11` болохгүй (`xlsx.ts`).
 */
import { Decimal } from 'decimal.js';
import { ulaanbaatarDateKey } from './revenue.js';
import { ulaanbaatarDateTimeText } from './sales-export.js';
import { xlsxFile } from './xlsx.js';
/**
 * Каталог. Шинэ тайлан нэмэхэд ЭНД мөр нэмж, `services/core`-ийн
 * `report-files.ts`-д мөр бэлдэгч бичнэ — AI туслах өөрөө таньж эхэлнэ.
 *
 * ⚠️ Өртөг, ашгийн багана эрхгүй хүнд (`canSeeCost`) ГАРАХГҮЙ — эрхийн
 *    жагсаалтаас үл хамааран сервер хасна.
 */
export const REPORT_FILES = {
    sales: {
        title: 'Борлуулалт — гутал бүрээр',
        about: 'Зарсан гутал бүр: огноо, захиалга, салбар, худалдагч, баркод, размер, суваг, төлбөр, үнэ, хөнгөлөлт, дүн, баталгаа дуусах өдөр, буцаагдсан эсэх; эрхтэй бол өртөг, ашиг, урамшуулал.',
        perms: ['reports.view', 'finance.view'],
    },
    orders: {
        title: 'Захиалга',
        about: 'Захиалга бүр: огноо, код, салбар, худалдагч, суваг, ширхэг, дэд дүн, хөнгөлөлт, НӨАТ, нийт, төлбөрийн хэлбэр, төлөв (идэвхтэй/хүчингүй, шалтгаан).',
        perms: ['reports.view', 'finance.view', 'orders.view'],
    },
    payments: {
        title: 'Төлбөр — хэлбэрээр',
        about: 'Төлбөрийн мөр бүр: огноо, захиалга, салбар, хэлбэр (бэлэн, карт, QPay, шилжүүлэг, зээлийн апп, хувь зээл, солилцоо), зээлийн аппын нэр, дүн, захиалгын төлөв.',
        perms: ['reports.view', 'finance.view'],
    },
    returns: {
        title: 'Буцаалт, солилцоо',
        about: 'Буцаалт бүр: огноо, төрөл (буцаалт/солилцоо), анхны захиалга, баркод, шалтгаан, шийдвэр (тавиурт буцсан, гэмтэлтэй г.м.), буцаасан мөнгө, хэлбэр, бүртгэсэн ажилтан.',
        perms: ['reports.view', 'finance.view', 'orders.refund'],
    },
    finance: {
        title: 'Санхүүгийн хураангуй — сараар',
        about: 'Сар бүр: зарсан ширхэг, борлуулалтын орлого, хөнгөлөлт, НӨАТ (и-баримт), буцаасан мөнгө; эрхтэй бол зарсан гутлын өртөг, бохир ашиг, худалдан авалтын зардал (бараа, тээвэр, гааль, бусад), актлалтын алдагдал.',
        perms: ['finance.view', 'reports.view'],
    },
    purchases: {
        title: 'Аялал, худалдан авалт',
        about: 'Аялал бүр: код, огноо, нийлүүлэгч, төлөв, гутлын тоо, барааны дүн (₽), зээлээр эсэх, төлсөн дүн, тээвэр, гааль, бусад зардал (₮), зээлийн эцсийн хугацаа.',
        perms: ['purchasing.view', 'finance.view', 'credit.view'],
    },
    supplier_payments: {
        title: 'Нийлүүлэгчийн зээлийн төлөлт',
        about: 'Нийлүүлэгчид хийсэн төлбөр бүр: огноо, аялал, нийлүүлэгч, дүн (₽), тэмдэглэл, бүртгэсэн ажилтан, цуцлагдсан эсэх.',
        perms: ['credit.view', 'finance.view'],
    },
    write_offs: {
        title: 'Актлалт',
        about: 'Актласан гутал бүр: огноо, баркод, размер, шалтгаан, тэмдэглэл, баталсан ажилтан; эрхтэй бол алдагдлын дүн.',
        perms: ['finance.view', 'items.view'],
    },
    stock: {
        title: 'Нөөц — одоо байгаа гутал',
        about: 'Зарагдаагүй гутал бүр: баркод, размер, төлөв, салбар, байршил, үнэ, нийлүүлэгч, бүртгэсэн огноо; эрхтэй бол өртөг.',
        perms: ['items.view', 'reports.view'],
    },
    transfers: {
        title: 'Шилжүүлэг',
        about: 'Шилжүүлэг бүр: код, хаанаас, хаашаа, төлөв, илгээсэн/хүлээн авсан ширхэг, огноо, илгээсэн ба хүлээн авсан ажилтан.',
        perms: ['transfers.view'],
    },
    installments: {
        title: 'Хувь зээл',
        about: 'Зээл бүр: нээсэн огноо, захиалга, салбар, худалдан авагч, утас, регистр, урьдчилгаа, зээлийн дүн, хугацаа (сар), төлсөн, үлдэгдэл, хаагдсан эсэх.',
        perms: ['installments.view'],
    },
    warranty_claims: {
        title: 'Баталгааны нэхэмжлэл',
        about: 'Нэхэмжлэл бүр: код, огноо, баркод, нийлүүлэгч, гэмтэл, тайлбар, шийдвэр (засвар, солилт, буцаалт, татгалзсан), засварын зардал, буцаасан мөнгө, шийдсэн огноо.',
        perms: ['warranty.view'],
    },
    customers: {
        title: 'Худалдан авагч',
        about: 'Худалдан авагч бүр: нэр, утас, регистр, и-мэйл, бүртгэгдсэн огноо, захиалгын тоо, маркетингийн зөвшөөрөл.',
        // ⚠️ `customers.view` БИШ: тэр нь касс дээр НЭГ хүнийг утсаар хайх эрх
        //    (худалдагчид бий). Бүх хүний утас, регистрийг бөөнөөр татах нь
        //    хувийн мэдээллийн эрсдэл — тайлан харах эрхтэйд л.
        perms: ['reports.view'],
    },
};
export const REPORT_KINDS = Object.keys(REPORT_FILES);
export function isReportKind(v) {
    return Object.hasOwn(REPORT_FILES, v);
}
/** Тухайн хэрэглэгчийн татаж болох тайлангууд — эрхийн жагсаалтаас. */
export function allowedReportKinds(perms) {
    return REPORT_KINDS.filter((k) => REPORT_FILES[k].perms.some((p) => perms.has(p)));
}
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;
function cell(v, kind) {
    if (v === null || v === '')
        return null;
    if (v instanceof Date) {
        return { text: kind === 'date' ? ulaanbaatarDateKey(v) : ulaanbaatarDateTimeText(v) };
    }
    if (kind === 'money' || kind === 'int') {
        const s = String(v);
        // Тоо биш утга (ж: «—») текстээр — файл эвдрэхгүй
        if (!PLAIN_NUMBER.test(s))
            return { text: s };
        return kind === 'money' ? { num: s, style: 'money' } : { num: s };
    }
    return { text: String(v) };
}
/** Excel-ийн хуудасны нэр — 31 тэмдэгт, `[]:*?/\` байхгүй. */
function sheetName(title) {
    return title.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31).trim() || 'Тайлан';
}
/**
 * Ерөнхий хүснэгт → нэг хуудас:
 *   1. гарчиг · 2. «Үүсгэсэн … · Бүх хугацаа · N мөр» (+ тайлбар)
 *   4. баганын толгой · дараа нь мөрүүд · төгсгөлд «Нийт» (sum багана байвал)
 */
export function reportSheet(t) {
    const kinds = t.columns.map((c) => c.kind ?? 'text');
    const meta = `Үүсгэсэн: ${ulaanbaatarDateTimeText(t.generatedAt)} · Бүх хугацаа · ${t.rows.length} мөр`;
    const rows = [
        [{ text: t.title, style: 'title' }],
        [{ text: meta, style: 'label' }],
        ...(t.note ? [[{ text: t.note, style: 'alert' }]] : []),
        [],
        t.columns.map((c) => ({ text: c.header, style: 'header' })),
        ...t.rows.map((r) => t.columns.map((_, i) => cell(r[i] ?? null, kinds[i]))),
    ];
    if (t.columns.some((c) => c.sum) && t.rows.length > 0) {
        rows.push(t.columns.map((c, i) => {
            if (i === 0)
                return { text: 'Нийт', style: 'label' };
            if (!c.sum)
                return null;
            const total = t.rows.reduce((acc, r) => {
                const v = r[i];
                return v !== null && v !== undefined && PLAIN_NUMBER.test(String(v)) ? acc.plus(String(v)) : acc;
            }, new Decimal(0));
            return kinds[i] === 'money'
                ? { num: total.toFixed(2), style: 'money' }
                : { num: total.toFixed(0), style: 'label' };
        }));
    }
    return { name: sheetName(t.title), widths: t.columns.map((c) => c.width), rows };
}
export function reportXlsx(t) {
    return xlsxFile(reportSheet(t));
}
/** `sales_2026-10-06.xlsx` — УБ-ийн өдрөөр. */
export function reportFileName(kind, at) {
    return `${kind}_${ulaanbaatarDateKey(at)}.xlsx`;
}
//# sourceMappingURL=report-files.js.map